/**
 * Australia-specific constants & validators.
 * Source of truth for AHPRA, states, postcodes and visa categories.
 */

export const AU_STATES = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NT', name: 'Northern Territory' },
] as const;

export type AuStateCode = (typeof AU_STATES)[number]['code'];

/** AHPRA registration numbers begin with the profession prefix MED for medical practitioners,
 * followed by 10 digits. */
const AHPRA_REGEX = /^MED\d{10}$/;
export function isValidAhpra(value: string): boolean {
  return AHPRA_REGEX.test(value.trim().toUpperCase());
}

export const RESIDENCY_STATUSES = [
  { code: 'citizen', label: 'Australian citizen' },
  { code: 'permanent_resident', label: 'Permanent resident' },
  { code: 'visa_482', label: 'Subclass 482 (Skills-in-demand)' },
  { code: 'visa_186', label: 'Subclass 186 (Employer Nomination)' },
  { code: 'visa_189', label: 'Subclass 189 (Skilled Independent)' },
  { code: 'visa_190', label: 'Subclass 190 (State Nominated)' },
  { code: 'visa_407', label: 'Subclass 407 (Training visa)' },
  { code: 'visa_485', label: 'Subclass 485 (Temporary Graduate)' },
  { code: 'visa_other', label: 'Other visa' },
] as const;

export type ResidencyCode = (typeof RESIDENCY_STATUSES)[number]['code'];

export const SENIORITY_LEVELS = [
  { code: 'intern', label: 'Intern (PGY1)' },
  { code: 'rmo', label: 'Resident Medical Officer (PGY2+)' },
  { code: 'registrar', label: 'Registrar' },
  { code: 'senior_registrar', label: 'Senior Registrar' },
  { code: 'fellow', label: 'Fellow' },
  { code: 'cmo', label: 'Career Medical Officer' },
  { code: 'consultant', label: 'Consultant / Specialist' },
  { code: 'gp', label: 'General Practitioner' },
  { code: 'vmo', label: 'Visiting Medical Officer' },
] as const;

export type SeniorityCode = (typeof SENIORITY_LEVELS)[number]['code'];

export const SPECIALTIES = [
  'Anaesthetics',
  'Cardiology',
  'Dermatology',
  'Emergency Medicine',
  'Endocrinology',
  'Gastroenterology',
  'General Practice',
  'General Surgery',
  'Geriatric Medicine',
  'Haematology',
  'Infectious Diseases',
  'Intensive Care',
  'Neurology',
  'Obstetrics & Gynaecology',
  'Oncology',
  'Ophthalmology',
  'Orthopaedic Surgery',
  'Otolaryngology (ENT)',
  'Paediatrics',
  'Pathology',
  'Plastic Surgery',
  'Psychiatry',
  'Radiology',
  'Rehabilitation Medicine',
  'Renal Medicine',
  'Respiratory Medicine',
  'Rheumatology',
  'Rural Generalist',
  'Urology',
  'Vascular Surgery',
] as const;

export type Specialty = (typeof SPECIALTIES)[number];

/** Approximate AU postcode validator (4 digits). */
export function isValidPostcode(value: string): boolean {
  return /^\d{4}$/.test(value.trim());
}

/** AU mobile / landline e.164-ish lightweight check. */
export function isValidAuPhone(value: string): boolean {
  const cleaned = value.replace(/[\s()-]/g, '');
  return /^(\+61|0)[2-478]\d{8}$/.test(cleaned);
}
